import { absoluteUrl } from '@constants/site';
import { declension } from '@utils/declension';
import { organizationRef } from './organization';
import { buildAggregateRating } from './product';
import {
  isoDateTime,
  isoDuration,
  onlyStrings,
  stripTags,
  toNumber,
  truncate
} from './text';
import type { JsonLdDocument, JsonLdNode } from './types';
import { SCHEMA_CONTEXT } from './types';

export interface RecipeJsonLdSource {
  title?: string | null;
  text?: string | null;
  date?: string | null;
  rating?: number | null;
  rating_votes?: number | null;
  cooking_time?: number | null;
  serving?: number | null;
  calories?: number | string | null;
  protein?: number | string | null;
  fat?: number | string | null;
  carbohydrates?: number | string | null;
  recipeIngredients?: Array<{ title?: string | null } | null> | null;
  recipeInstructions?: Array<{
    id?: string | number | null;
    title?: string | null;
    mainImage?: string | null;
  } | null> | null;
  authorItem?: { fio?: string | null; identify?: string | null } | null;
  bludoItem?: { title?: string | null } | null;
  typesItem?: { title?: string | null } | null;
  attachmentsItem?: {
    cacheItem?: Array<{ filePath?: string | null } | null> | null;
  } | null;
}

export interface RecipeJsonLdOptions {
  url: string;
  video?: JsonLdNode | null;
}

const MAX_DESCRIPTION = 1000;
const MAX_STEP_NAME = 60;

const FIRST_SENTENCE_RE = /^[\s\S]*?[.!?](?=\s+[A-ZА-ЯЁ]|\s*$)/;

const stepName = (text: string): string => {
  const sentence = FIRST_SENTENCE_RE.exec(text)?.[0] ?? text;
  return truncate(sentence.replace(/[.!?:;,\s]+$/, ''), MAX_STEP_NAME);
};

const gramsOf = (
  value: number | string | null | undefined
): string | undefined => {
  const numeric = toNumber(value);
  return numeric === null ? undefined : `${numeric} г`;
};

const buildNutrition = (source: RecipeJsonLdSource): JsonLdNode | null => {
  const calories = toNumber(source.calories);
  if (calories === null || calories <= 0) {
    return null;
  }
  return {
    '@type': 'NutritionInformation',
    calories: `${calories} calories`,
    servingSize: '100 г',
    proteinContent: gramsOf(source.protein),
    fatContent: gramsOf(source.fat),
    carbohydrateContent: gramsOf(source.carbohydrates)
  };
};

const buildAuthor = (source: RecipeJsonLdSource): JsonLdNode => {
  const fio = source.authorItem?.fio?.trim();
  if (!fio) {
    return organizationRef();
  }
  const identify = source.authorItem?.identify?.trim();
  return {
    '@type': 'Person',
    name: fio,
    url: identify ? absoluteUrl(`/author/${identify}`) : undefined
  };
};

export const buildRecipe = (
  source: RecipeJsonLdSource | null | undefined,
  { url, video }: RecipeJsonLdOptions
): JsonLdDocument | null => {
  const name = source?.title?.trim();
  if (!source || !name) {
    return null;
  }

  const steps = (source.recipeInstructions ?? [])
    .map(step => ({
      id: step?.id,
      text: stripTags(step?.title),
      image: step?.mainImage?.trim() || undefined
    }))
    .filter(step => step.text !== '');

  const image = onlyStrings([
    ...(source.attachmentsItem?.cacheItem ?? []).map(item => item?.filePath),
    ...steps.map(step => step.image)
  ]);
  if (image.length === 0) {
    return null;
  }

  const description = truncate(stripTags(source.text), MAX_DESCRIPTION);
  const ingredients = (source.recipeIngredients ?? [])
    .map(item => stripTags(item?.title))
    .filter(title => title !== '');
  const serving = toNumber(source.serving);
  const cookingTime = toNumber(source.cooking_time);
  const halfTime = cookingTime ? isoDuration(cookingTime / 2) : null;
  const nutrition = buildNutrition(source);

  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Recipe',
    '@id': `${url}#recipe`,
    url,
    mainEntityOfPage: url,
    name,
    image,
    description: description || undefined,
    author: buildAuthor(source),
    datePublished: isoDateTime(source.date) ?? undefined,
    recipeCuisine: source.typesItem?.title?.trim() || undefined,
    recipeCategory: source.bludoItem?.title?.trim() || undefined,
    prepTime: halfTime ?? undefined,
    cookTime: halfTime ?? undefined,
    totalTime: cookingTime ? isoDuration(cookingTime) ?? undefined : undefined,
    recipeYield:
      serving && serving > 0
        ? [
            `${serving}`,
            `${serving} ${declension(serving, ['порция', 'порции', 'порций'])}`
          ]
        : undefined,
    recipeIngredient: ingredients.length ? ingredients : undefined,
    recipeInstructions: steps.length
      ? steps.map(
          (step): JsonLdNode => ({
            '@type': 'HowToStep',
            name: stepName(step.text),
            text: step.text,
            url:
              step.id === null || step.id === undefined
                ? undefined
                : `${url}#${step.id}`,
            image: step.image
          })
        )
      : undefined,
    nutrition: serving && serving > 0 && nutrition ? nutrition : undefined,
    aggregateRating:
      buildAggregateRating(source.rating, source.rating_votes, 'ratingCount') ??
      undefined,
    video: video ?? undefined
  };
};
